import { useEffect, useState } from "react";
import { Button } from "@electro-planner/ui";
import type { SymbolDefinition } from "@electro-planner/symbols";

export type InspectorDevice = {
  id: string;
  designation: string;
  rotation: 0 | 90 | 180 | 270;
  symbol: Pick<SymbolDefinition, "id" | "label" | "designationPrefix" | "familyKey">;
  position: { x: number; y: number };
};

type Props = {
  device: InspectorDevice | null;
  isDesignationDuplicate: boolean;
  onRename: (id: string, designation: string) => void;
  onSetRotation: (id: string, rotation: 0 | 90 | 180 | 270) => void;
  onDelete: (id: string) => void;
};

const ROTATIONS: Array<0 | 90 | 180 | 270> = [0, 90, 180, 270];

/**
 * Högerpanel som visas när exakt en device är markerad. Tom view om inget är valt —
 * vi behåller panelen synlig för att hålla canvas-bredden stabil.
 */
export function DeviceInspector({
  device,
  isDesignationDuplicate,
  onRename,
  onSetRotation,
  onDelete,
}: Props) {
  // Lokal input-state så användaren kan skriva fritt utan att Yjs-roundtrippen
  // flyttar caret. Skrivs tillbaka på blur eller Enter.
  const [draftDesignation, setDraftDesignation] = useState(device?.designation ?? "");

  useEffect(() => {
    setDraftDesignation(device?.designation ?? "");
  }, [device?.id, device?.designation]);

  function commit() {
    if (!device) return;
    const trimmed = draftDesignation.trim();
    if (!trimmed || trimmed === device.designation) {
      setDraftDesignation(device.designation);
      return;
    }
    onRename(device.id, trimmed);
  }

  return (
    <aside className="no-print flex w-72 shrink-0 flex-col border-l border-[var(--color-border)] bg-[var(--color-surface)]">
      <div className="border-b border-[var(--color-border)] px-3 py-2">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted)]">
          Egenskaper
        </h2>
      </div>

      {!device ? (
        <div className="px-3 py-6 text-sm text-[var(--color-muted)]">
          Markera en symbol för att se och redigera dess egenskaper.
        </div>
      ) : (
        <div className="flex flex-col gap-4 px-3 py-3 text-sm">
          <Field label="Symbol">
            <div className="flex flex-col">
              <span>{device.symbol.label.sv}</span>
              <span className="text-xs text-[var(--color-muted)]">
                {device.symbol.designationPrefix} · {device.symbol.familyKey}
              </span>
            </div>
          </Field>

          <Field label="Beteckning">
            <input
              type="text"
              value={draftDesignation}
              onChange={(e) => setDraftDesignation(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  (e.target as HTMLInputElement).blur();
                }
                if (e.key === "Escape") {
                  setDraftDesignation(device.designation);
                  (e.target as HTMLInputElement).blur();
                }
              }}
              aria-invalid={isDesignationDuplicate}
              className="w-full rounded border bg-white px-2 py-1 text-sm outline-none focus:ring-2"
              style={{
                borderColor: isDesignationDuplicate
                  ? "#dc2626"
                  : "var(--color-border)",
              }}
            />
            {isDesignationDuplicate ? (
              <p className="mt-1 text-xs text-red-600">
                Beteckningen används redan på det här arket.
              </p>
            ) : null}
          </Field>

          <Field label="Rotation">
            <div className="flex gap-1">
              {ROTATIONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => onSetRotation(device.id, r)}
                  className="flex-1 rounded border px-2 py-1 text-xs focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-accent)]"
                  style={{
                    background: device.rotation === r ? "#0f766e" : "white",
                    color: device.rotation === r ? "white" : "var(--color-fg)",
                    borderColor:
                      device.rotation === r ? "#0f766e" : "var(--color-border)",
                  }}
                  aria-pressed={device.rotation === r}
                >
                  {r}°
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-[var(--color-muted)]">
              Tips: tryck <kbd className="rounded border border-[var(--color-border)] bg-white px-1 font-mono">R</kbd> för att rotera 90°.
            </p>
          </Field>

          <Field label="Position">
            <span className="font-mono text-xs text-[var(--color-muted)]">
              x {Math.round(device.position.x)} · y {Math.round(device.position.y)}
            </span>
          </Field>

          <div className="mt-2 border-t border-[var(--color-border)] pt-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(device.id)}
              title="Radera symbol (Delete)"
            >
              Radera symbol
            </Button>
          </div>
        </div>
      )}
    </aside>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wider text-[var(--color-muted)]">
        {label}
      </span>
      {children}
    </label>
  );
}
